import React, { SyntheticEvent, useCallback } from 'react';
import { ToastContentProps } from 'react-toastify';

import { removeApprovedEntitiesNotifications } from 'redux/reducers/notificationSlice';
import {
  approvedEntitiesDelayedNotificationIdsSelector,
  approvedEntitiesNotificationIdsSelector,
} from 'redux/selectors/notificationSelectors';
import BaseAlert from 'components/wrappers/BaseAlert';
import BaseToast from 'components/wrappers/BaseToast';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import CongratulationsModal from 'components/modals/CongratulationsModal';
import { BackofficeInstantNotificationsResponseModel } from 'api/backofficeNotificationApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EntityApprovedDelayedNotification.module.less';

type OwnProps = {
  toastId: string;
  approvedEntities: BackofficeInstantNotificationsResponseModel;
};

// ToastContentProps are embeded by toast when using custom component
type Props = OwnProps & Partial<ToastContentProps>;

export default function EntityApprovedDelayedNotification(props: Props) {
  const { approvedEntities, toastId } = props;
  const { assignmentNotifications, customerNotifications } = approvedEntities;
  const dispatch = useAppDispatch();
  const approvedEntitiesNotificationIds = useAppSelector(
    approvedEntitiesNotificationIdsSelector,
  );
  const approvedEntitiesDelayedNotificationIds = useAppSelector(
    approvedEntitiesDelayedNotificationIdsSelector,
  );

  const closeApprovedPopup = () => {
    BaseToast.dismiss(toastId);
  };

  const markAllNotificationsAsRead = useCallback(async () => {
    // Do not call API if all notifications are already read
    if (!approvedEntitiesNotificationIds.length) return;

    const result = await dispatch(
      removeApprovedEntitiesNotifications(approvedEntitiesNotificationIds),
    );

    if (!removeApprovedEntitiesNotifications.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [approvedEntitiesNotificationIds, dispatch]);

  const markDelayedNotificationsAsRead = useCallback(async () => {
    // Do not call API if delayed notifications are already read
    if (!approvedEntitiesDelayedNotificationIds.length) return;

    const result = await dispatch(
      removeApprovedEntitiesNotifications(
        approvedEntitiesDelayedNotificationIds,
      ),
    );

    if (!removeApprovedEntitiesNotifications.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [approvedEntitiesDelayedNotificationIds, dispatch]);

  const handleClose = () => {
    markDelayedNotificationsAsRead();
    closeApprovedPopup();
  };

  const handleCustomerKeyClick = async (
    event: SyntheticEvent,
    customerKey: string,
  ) => {
    event.preventDefault();

    await markAllNotificationsAsRead();

    window.open(`/customer/${customerKey}`, '_newtab' + Date.now());
  };

  const handleAssignmentKeyClick = async (
    event: SyntheticEvent,
    assignmentKey: string,
  ) => {
    event.preventDefault();

    await markAllNotificationsAsRead();

    window.open(`/assignment/${assignmentKey}`, '_newtab' + Date.now());
  };

  const getApprovedEntitiesNames = () => {
    let approvedEntitiesNames: string[] = [];

    if (!!assignmentNotifications?.length) {
      approvedEntitiesNames.push('assignments');
    }

    if (!!customerNotifications?.length) {
      approvedEntitiesNames.push('customers');
    }

    return approvedEntitiesNames.join(' & ');
  };

  const getDelayedNotificationContent = () => {
    const approvedEntitiesNames = getApprovedEntitiesNames();

    return (
      <>
        <div className={styles.popupCapture}>
          Your {approvedEntitiesNames} were approved, while you were away.
        </div>

        {!!assignmentNotifications?.length && (
          <div className={styles.popupTextWrap}>
            <div className={styles.popupTitle}>Assignments:</div>
            <div>
              {assignmentNotifications
                .map<React.ReactNode>((assignmentNotification, index) => (
                  <a
                    href="/"
                    className={styles.entityKey}
                    key={`${assignmentNotification.notificationId}-${index}`}
                    onClick={(e) =>
                      handleAssignmentKeyClick(
                        e,
                        assignmentNotification.entityKey as string,
                      )
                    }
                  >
                    {assignmentNotification.workerName}
                  </a>
                ))
                .reduce((previous, current) => [previous, ', ', current])}
              .
            </div>
          </div>
        )}

        {!!customerNotifications?.length && (
          <div>
            <div className={styles.popupTitle}>Customers:</div>
            <div>
              {customerNotifications
                .map<React.ReactNode>((customerNotification, index) => (
                  <a
                    href="/"
                    className={styles.entityKey}
                    key={`${customerNotification.notificationId}-${index}`}
                    onClick={(e) =>
                      handleCustomerKeyClick(
                        e,
                        customerNotification.entityKey as string,
                      )
                    }
                  >
                    {customerNotification.entityKey}
                  </a>
                ))
                .reduce((previous, current) => [previous, ', ', current])}
              .
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <CongratulationsModal
      onClose={handleClose}
      content={
        <div className={styles.popupText}>
          {getDelayedNotificationContent()}
        </div>
      }
    />
  );
}
